/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState, useCallback } from "react";
import { authStore } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { Headphones, RefreshCw, Phone, PhoneCall, Circle, Voicemail, ExternalLink } from "lucide-react";
import { AgentWeeklyReport } from "../components/agent/AgentWeeklyReport";
import { RatingCompletion } from "../components/agent/RatingCompletion";
import { RatingCalibration } from "../components/agent/RatingCalibration";
import { CalibrationMonthly } from "../components/agent/CalibrationMonthly";
import { RatingSelfCheck } from "../components/agent/RatingSelfCheck";

const STATUS = {
  "in-call": { zh: "通话中", en: "In call", dot: "text-emerald-500", cls: "border-emerald-300 bg-emerald-50", pulse: true },  ringing: { zh: "响铃中", en: "Ringing", dot: "text-amber-500", cls: "border-amber-300 bg-amber-50", pulse: true },
  calling: { zh: "拨号中", en: "Dialing", dot: "text-amber-500", cls: "border-amber-300 bg-amber-50", pulse: true },
  idle: { zh: "空闲", en: "Available", dot: "text-emerald-500", cls: "border-emerald-200 bg-white", pulse: false },
  offline: { zh: "离线", en: "Offline", dot: "text-slate-300", cls: "border-slate-200 bg-slate-50", pulse: false },
};
const REGION = { la: "洛杉矶 LA", sd: "圣地亚哥 SD", norcal: "北加 NorCal", hq: "总部 HQ" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function ago(iso, zh) {
  if (!iso) return zh ? "从未" : "never";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return zh ? `${s}秒前` : `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return zh ? `${m}分钟前` : `${m}m ago`;
  const h = Math.floor(m / 60);
  return zh ? `${h}小时前` : `${h}h ago`;
}

export default function AgentBoard() {
  const { api, lang, user, permOf } = useApp();
  const zh = lang === "zh";
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [online, setOnline] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [missed, setMissed] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/telephony/agents");
      setAgents(data.agents || []);
      setOnline(data.online || 0);
      setTotal(data.total || 0);
      setDenied(false);
      try { const { data: mc } = await api.get("/telephony/calls", { params: { needs_callback: true, limit: 20 } }); setMissed(mc.calls || []); } catch { /* ignore */ }
    } catch (e) { if (e?.response?.status === 403) setDenied(true); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (denied) return undefined; const t = setInterval(load, 8000); return () => clearInterval(t); }, [load, denied]);

  const dial = (number, name) => { if (number) window.dispatchEvent(new CustomEvent("softphone:dial", { detail: { number, name } })); };

  if (denied) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="agent-board-denied">
        <Headphones className="h-10 w-10 text-slate-300 mb-3" />
        <p className="text-lg font-semibold text-slate-700">{zh ? "无权访问坐席看板" : "Access denied"}</p>
        <p className="text-sm text-slate-400 mt-1">{zh ? "该页面仅对经理 / 管理员开放。" : "This board is for managers only."}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="agent-board-page">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="h-9 w-9 rounded-xl bg-brand-grad shadow-brand flex items-center justify-center"><Headphones className="h-5 w-5 text-white" /></span>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{zh ? "坐席看板" : "Agent Board"}</h1>
        <span data-testid="agents-online-count" className="inline-flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
          <Circle className="h-2.5 w-2.5 fill-emerald-500 text-emerald-500" />
          {zh ? "在线" : "Online"} {online} / {total}
        </span>
        <button data-testid="agents-refresh" onClick={load}
          className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto btn-soft !py-1.5">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />{zh ? "刷新" : "Refresh"}
        </button>
      </div>
      <p className="text-xs text-slate-400">{zh ? "每 8 秒自动刷新 · 显示销售软电话的在线与通话状态，便于派单 · 点击坐席卡片查看电话信息、短信 / 通话记录与通话质量评价" : "Auto-refreshes every 8s · click an agent for phone info, SMS / call history and quality reviews"}</p>

      <AgentWeeklyReport api={api} zh={zh} />
      {/* 【第六批】评分统计/校准三块改读 /agents::telephony-stats（与后端同一判定） */}
      {(permOf("/agents::telephony-stats") ?? ["admin", "ceo", "sales_vp"].includes(user?.role)) && <RatingCompletion api={api} zh={zh} />}
      {(permOf("/agents::telephony-stats") ?? ["admin", "ceo", "sales_vp"].includes(user?.role)) && <RatingCalibration api={api} zh={zh} />}
      {(permOf("/agents::telephony-stats") ?? ["admin", "ceo", "sales_vp"].includes(user?.role)) && <CalibrationMonthly api={api} zh={zh} />}
      {["sales_manager", "sales_vp"].includes(user?.role) && <RatingSelfCheck api={api} zh={zh} />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {agents.map((a) => {
          const s = STATUS[a.status] || STATUS.offline;
          return (
            <div key={a.username} data-testid={`agent-card-${a.username}`} role="button" tabIndex={0}
              onClick={() => navigate(`/agents/${a.username}`)} onKeyDown={(e) => { if (e.key === "Enter") navigate(`/agents/${a.username}`); }}
              className={`rounded-xl border p-4 transition-colors cursor-pointer hover:shadow-md hover:border-amber-300 ${s.cls}`}>
              <div className="flex-wrap md:flex-nowrap flex items-center gap-3">
                <div className="relative">
                  {a.avatarFileId
                    ? <img src={`${BACKEND}/api/files/${a.avatarFileId}/download?auth=${authStore.get("ali_token") || ""}`} alt={a.name}
                        className="h-11 w-11 rounded-full object-cover border border-slate-200" data-testid={`agent-avatar-img-${a.username}`} />
                    : <div className="h-11 w-11 rounded-full bg-slate-800 text-white flex items-center justify-center font-bold">
                        {(a.name || a.username).slice(0, 1).toUpperCase()}
                      </div>}
                  <Circle className={`absolute -bottom-0.5 -right-0.5 h-4 w-4 ${s.dot} ${a.online ? "fill-current" : ""} ${s.pulse ? "animate-pulse" : ""}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">{a.name}</p>
                  <p className="text-xs text-slate-500">{REGION[a.region] || a.region || "—"}</p>
                </div>
              </div>
              <div className="flex-wrap md:flex-nowrap mt-3 flex items-center justify-between">
                <span data-testid={`agent-status-${a.username}`}
                  className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-1 ${a.online ? (a.status === "in-call" || a.status === "ringing" || a.status === "calling" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800") : "bg-slate-100 text-slate-500"}`}>
                  {(a.status === "in-call") ? <PhoneCall className="h-3 w-3" /> : (a.status === "idle" ? <Phone className="h-3 w-3" /> : null)}
                  {zh ? s.zh : s.en}
                </span>
                <span className="text-[11px] text-slate-400">{a.online ? "" : ago(a.lastSeen, zh)}</span>
              </div>
              {a.online && a.peer && (a.status === "in-call" || a.status === "ringing") && (
                <p className="mt-2 text-xs text-slate-500 font-mono truncate">→ {a.peer}</p>
              )}
              <p className="mt-2 text-[11px] text-amber-700 font-medium inline-flex items-center gap-0.5" data-testid={`agent-open-${a.username}`}>
                {zh ? "查看电话 / 短信 / 通话质量 →" : "Phone · SMS · quality →"}
              </p>
            </div>
          );
        })}
        {agents.length === 0 && !loading && (
          <p className="col-span-full text-center text-slate-400 py-10" data-testid="agents-empty">{zh ? "暂无坐席" : "No agents"}</p>
        )}
      </div>

      <div data-testid="missed-callbacks-panel" className="rounded-xl border border-amber-200 bg-white overflow-hidden">
        <div className="flex-wrap md:flex-nowrap flex items-center gap-2 px-4 py-3 border-b border-amber-100 bg-amber-50/60">
          <PhoneCall className="h-4 w-4 text-amber-600" />
          <h2 className="text-sm font-semibold text-slate-800">{zh ? "待回拨（未接来电 / 语音留言）" : "Needs callback"}</h2>
          <span className="text-xs text-slate-500">{missed.length}</span>
          <button data-testid="missed-view-all" onClick={() => navigate("/calls")} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
            <ExternalLink className="h-3.5 w-3.5" />{zh ? "全部通话" : "All calls"}
          </button>
        </div>
        {missed.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-slate-400" data-testid="missed-empty">{zh ? "没有待回拨的来电 ✓" : "No callbacks pending ✓"}</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {missed.map((c) => (
              <div key={c.id} data-testid={`missed-row-${c.id}`} className="flex items-center gap-3 px-4 py-2.5 flex-wrap">
                {c.isVoicemail && <Voicemail className="h-4 w-4 text-purple-500 shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm text-slate-800 truncate">
                    {c.leadId ? (
                      <button onClick={() => navigate(`/crm/leads/${c.leadId}`)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-amber-700 hover:underline">{c.name || c.customerNumber}</button>
                    ) : (c.name || c.customerNumber)}
                  </p>
                  <p className="text-xs text-slate-400 font-mono">{c.customerNumber} · {(() => { try { return new Date(c.createdAt).toLocaleString(); } catch { return c.createdAt; } })()}</p>
                </div>
                <button data-testid={`missed-callback-${c.id}`} onClick={() => dial(c.customerNumber, c.name)}
                  className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-1.5">
                  <PhoneCall className="h-3.5 w-3.5" />{zh ? "回拨" : "Call"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
